// components/HeroSection/HeroSection.js
import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  ChevronRight, 
  Zap, 
  CheckCircle, 
  Code2, 
  Database, 
  Cpu, 
  Shield, 
  Globe, 
  Users,
  Activity,
  Server,
  Brain,
  Layers,
  Monitor
} from 'lucide-react';
import './HeroSection.css';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Technical animations data
  const techStack = [
    { name: 'React', progress: 95, color: '#61DAFB' },
    { name: 'Node.js', progress: 90, color: '#339933' },
    { name: 'AI/ML', progress: 85, color: '#FF6B6B' },
    { name: 'Cloud', progress: 92, color: '#4285F4' },
    { name: 'Security', progress: 98, color: '#FF9500' }
  ];

  const statsData = [
    { 
      number: '17+', 
      label: 'Institutions Served',
      icon: <Users className="w-6 h-6" />,
      color: 'blue'
    },
    { 
      number: '99.9%', 
      label: 'Uptime Guarantee',
      icon: <Activity className="w-6 h-6" />,
      color: 'green'
    },
    { 
      number: '24/7', 
      label: 'Support Available',
      icon: <Shield className="w-6 h-6" />,
      color: 'purple'
    }
  ];

  const codeLines = [
    'class ERPSystem extends AdvancedFramework {',
    '  constructor() {',
    '    this.features = [',
    '      "facial_recognition",',
    '      "rfid_attendance",',
    '      "ai_analytics"',
    '    ];',
    '  }',
    '  deploy() { return "success"; }',
    '}'
  ];

  const floatingElements = [
    { icon: <Code2 />, delay: 0, position: { top: '15%', left: '10%' } },
    { icon: <Database />, delay: 1, position: { top: '60%', left: '85%' } },
    { icon: <Cpu />, delay: 2, position: { top: '30%', left: '80%' } },
    { icon: <Globe />, delay: 3, position: { top: '68%', left: '40%' } },
    { icon: <Brain />, delay: 4, position: { top: '15%', left: '75%' } },
    { icon: <Server />, delay: 5, position: { top: '80%', left: '70%' } }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Mouse movement tracking for parallax
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) / 50,
        y: (e.clientY - window.innerHeight / 2) / 50
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section className="hero" role="banner" aria-label="Hero section introducing Roots of Northeast (ROne) ERP systems and AI technology solutions">
      {/* Animated Background */}
      <div className="hero__background" aria-hidden="true">
        <div className="hero__grid-overlay"></div>
        <div className="hero__floating-particles">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="hero__particle"
              style={{
                '--delay': `${i * 0.5}s`,
                '--duration': `${3 + (i % 3)}s`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
        
        {/* Floating Tech Icons */}
        {floatingElements.map((element, index) => (
          <div
            key={index}
            className="hero__floating-icon"
            aria-hidden="true"
            style={{
              ...element.position,
              '--delay': `${element.delay}s`,
              transform: `translate(${mousePosition.x * (index % 2 === 0 ? 1 : -1) * 0.5}px, ${mousePosition.y * (index % 2 === 0 ? 1 : -1) * 0.5}px)`
            }}
          >
            {element.icon}
          </div>
        ))}
      </div>

      <div className="hero__container">
        <div className="hero__content">
          {/* Left Content */}
          <div className={`hero__text ${isVisible ? 'hero__text--visible' : ''}`}>
            <div className="hero__badge">
              <Zap className="hero__badge-icon" aria-hidden="true" />
              <span>Advanced Technology Solutions</span>
              <div className="hero__badge-pulse" aria-hidden="true"></div>
            </div>
            
            <h1 className="hero__title">
              <span className="hero__title-line">Roots of Northeast(ROne)</span>
              <span className="hero__title-gradient">
                <span className="hero__title-gradient-text">Digital Future</span>
                <div className="hero__title-underline" aria-hidden="true"></div>
              </span>
              <div className="hero__title-tech-stack">
                <div className="hero__tech-indicator">
                  <span className="hero__tech-label">Most Innovative IT Company in</span>
                  <span className="hero__tech-name">BTC Kokrajhar & Northeast India</span>
                </div>
              </div>
            </h1>
            
            <p className="hero__description">
              🏆 Leading <strong>IT company in BTC Kokrajhar</strong> and <strong>Northeast India</strong>. <strong>Roots of Northeast (ROne)</strong> provides world-class <strong>ERP systems</strong>, <strong>AI-powered attendance management</strong>, and <strong>facial recognition technology</strong> for educational institutions across <strong>Assam, Meghalaya, Manipur, Tripura</strong>. Experience 99.9% uptime with our advanced <strong>RFID solutions</strong> and digital transformation services.
            </p>
            
            <div className="hero__actions" role="group" aria-label="Call to action buttons">
              <button className="hero__cta-primary" aria-describedby="cta-primary-desc">
                <span>Get Started Today</span>
                <ArrowRight className="hero__cta-icon" aria-hidden="true" />
                <div className="hero__cta-ripple" aria-hidden="true"></div>
              </button>
              <div id="cta-primary-desc" className="sr-only">Contact us to start implementing ERP systems and AI technology solutions</div>
              
              <button className="hero__cta-secondary" aria-describedby="cta-secondary-desc">
                <span>Watch Demo</span>
                <ChevronRight className="hero__cta-icon" aria-hidden="true" />
              </button>
              <div id="cta-secondary-desc" className="sr-only">View demonstration of our ERP and AI attendance management systems</div>
            </div>

            <div className="hero__stats" role="region" aria-label="Company statistics and achievements">
              {statsData.map((stat, index) => (
                <div key={index} className={`hero__stat hero__stat--${stat.color}`}>
                  <div className="hero__stat-icon" aria-hidden="true">
                    {stat.icon}
                  </div>
                  <div className="hero__stat-content">
                    <div className="hero__stat-number" aria-label={`${stat.number} ${stat.label}`}>{stat.number}</div>
                    <div className="hero__stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Advanced Dashboard */}
          <div className={`hero__visual ${isVisible ? 'hero__visual--visible' : ''}`} aria-label="Code demonstration showing ERP system implementation">
            {/* Code Editor Mockup */}
            <div className="hero__code-editor">
              <div className="hero__code-header">
                <div className="hero__code-controls" aria-hidden="true">
                  <span className="hero__code-dot hero__code-dot--red"></span>
                  <span className="hero__code-dot hero__code-dot--yellow"></span>
                  <span className="hero__code-dot hero__code-dot--green"></span>
                </div>
                <div className="hero__code-title">
                  <Monitor className="w-4 h-4" aria-hidden="true" />
                  <span>rots-erp-system.js</span>
                </div>
              </div>
              <div className="hero__code-content">
                {codeLines.map((line, index) => (
                  <div
                    key={index}
                    className="hero__code-line"
                    style={{ '--delay': `${index * 0.1}s` }}
                  >
                    <span className="hero__code-number">{index + 1}</span>
                    <span className="hero__code-text">{line}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Metrics Dashboard */}
            <div className="hero__dashboard">
              <div className="hero__dashboard-header">
                <div className="hero__dashboard-title">
                  <Activity className="w-5 h-5" />
                  <span>System Overview</span>
                  <div className="hero__status-indicator hero__status-indicator--online"></div>
                </div>
              </div>
              
              <div className="hero__dashboard-content">
                <div className="hero__metric-card">
                  <div className="hero__metric-header">
                    <span>ERP Performance</span>
                    <span className="hero__metric-value">99.8%</span>
                  </div>
                  <div className="hero__metric-chart">
                    <div className="hero__chart-bar" style={{ '--height': '99%', '--delay': '0.5s' }}></div>
                    <div className="hero__chart-bar" style={{ '--height': '95%', '--delay': '0.7s' }}></div>
                    <div className="hero__chart-bar" style={{ '--height': '98%', '--delay': '0.9s' }}></div>
                    <div className="hero__chart-bar" style={{ '--height': '100%', '--delay': '1.1s' }}></div>
                  </div>
                </div>

                <div className="hero__metric-grid">
                  <div className="hero__metric-item">
                    <div className="hero__metric-icon hero__metric-icon--blue">
                      <Database className="w-4 h-4" />
                    </div>
                    <div className="hero__metric-info">
                      <span className="hero__metric-label">RFID Range</span>
                      <span className="hero__metric-number">12m</span>
                    </div>
                  </div>

                  <div className="hero__metric-item">
                    <div className="hero__metric-icon hero__metric-icon--purple">
                      <Brain className="w-4 h-4" />
                    </div>
                    <div className="hero__metric-info">
                      <span className="hero__metric-label">AI Accuracy</span>
                      <span className="hero__metric-number">99.95%</span>
                    </div>
                  </div>

                  <div className="hero__metric-item">
                    <div className="hero__metric-icon hero__metric-icon--green">
                      <Shield className="w-4 h-4" />
                    </div>
                    <div className="hero__metric-info">
                      <span className="hero__metric-label">Security</span>
                      <span className="hero__metric-number">256-bit</span>
                    </div>
                  </div>

                  <div className="hero__metric-item">
                    <div className="hero__metric-icon hero__metric-icon--orange">
                      <Layers className="w-4 h-4" />
                    </div>
                    <div className="hero__metric-info">
                      <span className="hero__metric-label">Platforms</span>
                      <span className="hero__metric-number">6+</span>
                    </div>
                  </div>
                </div>

                <div className="hero__tech-stack-display">
                  <div className="hero__tech-title">Tech Stack</div>
                  <div className="hero__tech-items">
                    {techStack.map((tech, index) => (
                      <div
                        key={tech.name}
                        className="hero__tech-item"
                        style={{ '--tech-color': tech.color }}
                      >
                        <span>{tech.name}</span>
                        <div className="hero__tech-bar">
                          <div 
                            className="hero__tech-fill"
                            style={{ '--progress': `${tech.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Status Cards */}
            <div className="hero__status-cards">
              <div className="hero__status-card hero__status-card--1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>All Systems Operational</span>
              </div>
              <div className="hero__status-card hero__status-card--2">
                <Activity className="w-4 h-4 text-blue-500" />
                <span>Real-time Monitoring</span>
              </div>
              <div className="hero__status-card hero__status-card--3">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span>AI Processing Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;